import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(
    () => (document.documentElement.dataset.theme as Theme) || "light"
  );
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem("ac-theme", theme);
    } catch {
      /* ignore */
    }
  }, [theme]);
  const toggle = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);
  return [theme, toggle];
}

const PIN_KEY = "ac-pins";

/** Pinned agent ids, persisted to localStorage. Used for the compare tray. */
export function usePins(): {
  pins: string[];
  toggle: (id: string) => void;
  clear: () => void;
  has: (id: string) => boolean;
} {
  const [pins, setPins] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(PIN_KEY) || "[]");
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(PIN_KEY, JSON.stringify(pins));
    } catch {
      /* ignore */
    }
  }, [pins]);
  const toggle = useCallback(
    (id: string) => setPins((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id])),
    []
  );
  const clear = useCallback(() => setPins([]), []);
  const has = useCallback((id: string) => pins.includes(id), [pins]);
  return { pins, toggle, clear, has };
}
